import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Cropper from 'react-easy-crop';
import axiosInstance from '../api/axiosInstance';
import getCroppedImg from '../utils/cropImageHelper';
import "../styles/editProfile.css";

function ProfileUpload() {
  const navigate = useNavigate();
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Handle file selection
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload the cropped image to Cloudflare R2 using presigned URL (like flashes flow)
  const handleUpload = async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    try {
      // 1) Convert the cropped area to a blob
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);

      // 2) Ask server for a presigned upload URL in the trippin_bananas bucket under users/
      const contentType = croppedBlob.type || 'image/jpeg';
      const genResp = await axiosInstance.post('/profile/generate-upload-url', {
        contentType,
      });
      const { uploadUrl, fileName } = genResp.data || {};
      if (!fileName) throw new Error('No fileName returned by server');

      // 3) Upload directly to R2 via PUT (no auth header needed)
      if (uploadUrl) {
        const putResp = await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': contentType,
          },
          body: croppedBlob,
        });
        if (!putResp.ok) {
          const txt = await putResp.text();
          throw new Error(`R2 upload failed: ${putResp.status} ${txt}`);
        }
      }

      // 4) Save the object key to DB so profile points to it
      await axiosInstance.post('/profile/save-profile-key', { fileName });

      // 5) Navigate away
      navigate('/home');
    } catch (err) {
      console.error('Upload error:', err);
      // TODO: show UI error toast
    }
  };

  // Skip uploading
  const handleSkip = () => {
    navigate(-1);
  };

  return (
    <div className="profile-upload-container">
      <h2>Upload your profile picture</h2>
      {!imageSrc && (
        <div className="file-input-container">
          <label className="file-input-label">
            Choose a file
            <input type="file" accept="image/*" onChange={handleFileChange} />
          </label>
        </div>
      )}

      {imageSrc && (
        <div className="cropper-container">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>
      )}

      <div className="button-group">
        <button className="upload-button" onClick={handleUpload} disabled={!imageSrc}>
          Upload
        </button>
        <button className="skip-button" onClick={handleSkip}>
          Skip
        </button>
      </div>
    </div>
  );
}

export default ProfileUpload;
